import { View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../components/PopupMatch2Base';
import { MagicLampsRewardCard } from './MagicLampsRewardCard';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { MagicLampsRays } from './MagicLampsRays';
import { magicLampsPlaySfx } from './magiclamps.utils';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';

interface PopupMatch2MagicLampsRewardsArgs extends PopupMatch2BaseInitOptions {
  rewards: Match2Reward[];
}

export default class PopupMatch2MagicLampsRewards extends PopupMatch2Base {
  private flexBox: FlexBoxView;
  private rays: MagicLampsRays;
  private cards: MagicLampsRewardCard[] = [];

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const boxWidth = 600;
    const boxHeight = 1017;

    const originalClose = opts.close;
    opts.close = async (success: boolean) => {
      this.rays.hideAnimated();
      await Promise.all(this.cards.map((card) => card.grantReward(this.root)));
      originalClose(success);
    };

    super({
      superview: opts.superview,
      zIndex: 10000,
      panelImageOpts: {
        image: ``,
        width: boxWidth,
        height: boxHeight,
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 60, right: 60 },
          vertical: { top: 60, bottom: 400 },
        },
      },
      offsetY: -250,
      title: {
        type: 'banner',
        localeText: () => i18n('match2.magicLamps.rewardsTitle'),
        color: 'red',
      },
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.POPUP,
      },
      cta: {
        localeText: () => i18n('match2.magicLamps.rewardsOk'),
        onClick: async () => opts.close?.(true),
        width: 340,
      },
    });

    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    this.rays = new MagicLampsRays({
      superview: this.panel,
      radius: 600,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5,
      width: opts.superview.style.width,
      height: opts.superview.style.height,
      opacity: 0.4,
      clip: true,
      zIndex: -1,
    });

    this.flexBox = new FlexBoxView({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height - 100,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
    });
  }

  public init(args: PopupMatch2MagicLampsRewardsArgs) {
    super.init(args);

    const rewards = args.rewards ?? [];
    let cardScale = 1;

    if (rewards.length <= 4) {
      cardScale = 1.2;
    } else if (rewards.length > 12) {
      cardScale = 0.7;
    } else if (rewards.length > 9) {
      cardScale = 0.8;
    }

    for (const reward of rewards) {
      const card = new MagicLampsRewardCard({
        width: 189 * cardScale,
        height: 230 * cardScale,
      });

      const node = getFlexNode(card);
      node.margin = 5;

      this.flexBox.addSubview(card);
      card.setup(reward);
      this.cards.push(card);
    }
  }

  public override async fadeIn(): Promise<void> {
    await super.fadeIn();
    magicLampsPlaySfx('tada');
    await this.rays.showAnimated();
  }
}
